import React, { useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useSubscription } from '../hooks/useSubscription';
import { SubscriptionService, PLANS } from '../services/SubscriptionService';
import PlanCard from '../components/subscription/PlanCard';

const SubscriptionPage = () => {
	const { isAuthenticated } = useAuth();
	const {
		subscription,
		isLoading: subscriptionLoading,
		hasActiveSubscription,
		getCurrentPlanName,
		getBillingInterval,
		isCancelingAtPeriodEnd,
		getPeriodEndDate,
		refreshSubscription
	} = useSubscription();

	// Get current billing interval from subscription
	const currentBillingInterval = getBillingInterval();
	
	// Map backend interval to frontend interval for selector
	const getFrontendInterval = (backendInterval) => {
		const map = {
			month: 'monthly',
			'4months': 'quarterly',
			year: 'yearly'
		};
		return map[backendInterval] || 'monthly';
	};

	const [billingInterval, setBillingInterval] = useState(
		currentBillingInterval ? getFrontendInterval(currentBillingInterval) : 'monthly'
	);
	const [isProcessing, setIsProcessing] = useState(false);
	const [error, setError] = useState(null);
	const [successMessage, setSuccessMessage] = useState(null);

	const currentPlanName = getCurrentPlanName();

	// Update billing interval selector when subscription loads
	React.useEffect(() => {
		if (currentBillingInterval && !subscriptionLoading) {
			setBillingInterval(getFrontendInterval(currentBillingInterval));
		}
	}, [currentBillingInterval, subscriptionLoading]);

	// Refresh subscription once when component mounts (in case webhook missed it)
	React.useEffect(() => {
		if (isAuthenticated) {
			// Only refresh once after a short delay to allow initial load
			const timer = setTimeout(() => {
				refreshSubscription();
			}, 2000); // Wait 2 seconds after mount

			return () => clearTimeout(timer);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []); // Empty dependency array - only run once on mount

	/**
	 * Handle plan selection (subscribe, upgrade, or downgrade)
	 */
	const handlePlanSelect = async (priceId, planName) => {
		try {
			setIsProcessing(true);
			setError(null);
			setSuccessMessage(null);

			// Build redirect URLs
			const baseUrl = window.location.origin;
			const successUrl = `${baseUrl}/subscription/success?session_id={CHECKOUT_SESSION_ID}`;
			const cancelUrl = `${baseUrl}/subscription/cancel`;

			// Create checkout session
			const response = await SubscriptionService.createCheckoutSession(
				priceId,
				successUrl,
				cancelUrl
			);

			if (response.status === 200 && response.url) {
				// Redirect to Stripe Checkout
				window.location.href = response.url;
			} else {
				throw new Error('Failed to create checkout session');
			}
		} catch (err) {
			console.error('Error creating checkout session:', err);
			setError(
				err.response?.data?.message ||
					err.message ||
					'Failed to start checkout. Please try again.'
			);
		} finally {
			setIsProcessing(false);
		}
	};

	/**
	 * Open Stripe Customer Portal for subscription management
	 */
	const handleManageSubscription = async () => {
		try {
			setIsProcessing(true);
			setError(null);

			const baseUrl = window.location.origin;
			const returnUrl = `${baseUrl}/subscription`;

			const response = await SubscriptionService.createPortalSession(returnUrl);

			if (response.status === 200 && response.url) {
				// Redirect to Stripe Customer Portal
				window.location.href = response.url;
			} else {
				throw new Error('Failed to create portal session');
			}
		} catch (err) {
			console.error('Error creating portal session:', err);
			setError(
				err.response?.data?.message ||
					err.message ||
					'Failed to open subscription management. Please try again.'
			);
		} finally {
			setIsProcessing(false);
		}
	};

	/**
	 * Format date for display
	 */
	const formatDate = (dateString) => {
		if (!dateString) return 'N/A';
		const date = new Date(dateString);
		return date.toLocaleDateString('en-US', {
			year: 'numeric',
			month: 'long',
			day: 'numeric'
		});
	};

	/**
	 * Format billing interval for display
	 */
	const formatBillingInterval = (interval) => {
		const intervalMap = {
			month: 'Monthly',
			'4months': 'Every 4 Months',
			year: 'Yearly'
		};
		return intervalMap[interval] || interval;
	};

	// Show loading while checking authentication
	if (!isAuthenticated) {
		return (
			<div className="flex min-h-screen items-center justify-center">
				<div className="text-center">
					<div className="mb-4 inline-block h-8 w-8 animate-spin rounded-full border-4 border-blue-600 border-t-transparent"></div>
					<p className="text-gray-600">Loading...</p>
				</div>
			</div>
		);
	}

	return (
		<div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
			<div className="mx-auto max-w-7xl">
				{/* Header */}
				<div className="mb-8 text-center">
					<h1 className="text-3xl font-bold text-gray-900">Subscription Plans</h1>
					<p className="mt-2 text-gray-600">
						Choose the plan that best fits your needs
					</p>
					
					{/* Current Plan Badge */}
					{hasActiveSubscription() && subscription && (
						<div className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-full shadow-lg">
							<svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
								<path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
							</svg>
							<span className="font-semibold">
								Active: {PLANS[currentPlanName]?.name} - {formatBillingInterval(subscription.billingInterval)}
							</span>
						</div>
					)}
				</div>

				{/* Current Subscription Info */}
				{hasActiveSubscription() && (
					<div className="mb-8 rounded-lg bg-blue-50 border border-blue-200 p-6">
						<div className="flex items-center justify-between">
							<div>
								<h2 className="text-lg font-semibold text-gray-900">
									Current Plan: {currentPlanName ? PLANS[currentPlanName]?.name : 'Unknown'}
									{subscription?.billingInterval && (
										<span className="ml-2 text-base font-normal text-gray-600">
											({formatBillingInterval(subscription.billingInterval)})
										</span>
									)}
								</h2>
								<p className="mt-1 text-sm text-gray-600">
									{isCancelingAtPeriodEnd()
										? `Your subscription will cancel on ${formatDate(getPeriodEndDate())}`
										: `Renews on ${formatDate(getPeriodEndDate())}`}
								</p>
							</div>
							<button
								onClick={handleManageSubscription}
								disabled={isProcessing}
								className="rounded-lg bg-blue-600 px-4 py-2 font-semibold text-white hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
							>
								{isProcessing ? 'Loading...' : 'Manage Subscription'}
							</button>
						</div>
					</div>
				)}

				{/* Billing Interval Selector */}
				<div className="mb-6 flex justify-center">
					<div className="inline-flex rounded-lg border border-gray-300 bg-white p-1">
						{['monthly', 'quarterly', 'yearly'].map((interval) => {
							// Map frontend interval to backend interval format
							const backendIntervalMap = {
								monthly: 'month',
								quarterly: '4months',
								yearly: 'year'
							};
							const backendInterval = backendIntervalMap[interval];
							const isCurrentInterval = subscription?.billingInterval === backendInterval;
							
							return (
								<button
									key={interval}
									onClick={() => setBillingInterval(interval)}
									className={`rounded-md px-4 py-2 text-sm font-medium transition-colors ${
										billingInterval === interval
											? 'bg-blue-600 text-white'
											: isCurrentInterval
											? 'bg-green-100 text-green-700 border border-green-300'
											: 'text-gray-700 hover:bg-gray-100'
									}`}
									title={isCurrentInterval ? 'Your current billing interval' : ''}
								>
									{interval.charAt(0).toUpperCase() + interval.slice(1)}
									{isCurrentInterval && ' ✓'}
								</button>
							);
						})}
					</div>
				</div>

				{/* Error Message */}
				{error && (
					<div className="mb-6 rounded-lg bg-red-50 border border-red-200 p-4">
						<p className="text-sm text-red-800">{error}</p>
					</div>
				)}

				{/* Success Message */}
				{successMessage && (
					<div className="mb-6 rounded-lg bg-green-50 border border-green-200 p-4">
						<p className="text-sm text-green-800">{successMessage}</p>
					</div>
				)}

				{/* Loading State */}
				{subscriptionLoading ? (
					<div className="flex justify-center py-12">
						<div className="text-gray-600">Loading subscription plans...</div>
					</div>
				) : (
					/* Plan Cards */
					<div className="grid gap-6 md:grid-cols-3">
						{Object.values(PLANS).map((plan) => {
							// Normalize plan name comparison (handle case differences)
							const planNameLower = plan.name.toLowerCase();
							const currentPlanLower = currentPlanName?.toLowerCase();
							const isCurrentPlan = currentPlanLower === planNameLower;
							
							// Check if this is the exact same plan and billing interval
							const backendIntervalMap = {
								monthly: 'month',
								quarterly: '4months',
								yearly: 'year'
							};
							const isExactMatch = isCurrentPlan && 
								subscription?.billingInterval === backendIntervalMap[billingInterval];
							
							// Get price ID for the selected billing interval
							const priceId = plan.priceIds[billingInterval];
							
							return (
								<PlanCard
									key={plan.name}
									plan={plan}
									billingInterval={billingInterval}
									isCurrentPlan={isCurrentPlan}
									isExactMatch={isExactMatch}
									isLoading={isProcessing}
									onSelect={priceId ? handlePlanSelect : undefined}
								/>
							);
						})}
					</div>
				)}

				{/* No Subscription Message */}
				{!hasActiveSubscription() && !subscriptionLoading && (
					<div className="mt-8 text-center">
						<p className="text-gray-600">
							You don't have an active subscription. Select a plan above to get started.
						</p>
					</div>
				)}
			</div>
		</div>
	);
};

export default SubscriptionPage;

